import React from 'react'
import PropTypes from 'prop-types'
import { Form, Field } from 'react-final-form'
import styles from './index.module.scss'
import {
  TextInput
} from '../TextInput'
import { enumToTitleCase } from '../../utilities/stringUtilities'
import { TextareaInput } from '../TextareaInput'
import { TimeInput } from '../TimeInput'
import { validate } from './utils'
import { dayOptions } from '../create-ticket-recurring/utils'
import { useSelector } from 'react-redux'
import classNames from 'classnames'
import DatePicker from 'react-datepicker'
import UnderlineSelect from '../underline-select'
import dayjs from '../../utilities/dayjs'
import { OnChange } from 'react-final-form-listeners'
import AssetFeesCard from '../add-fees-recurring/AddFeesCard'
import arrayMutators from 'final-form-arrays'
import { userSelectOptions } from '../../utilities/randomUtilities'
import { SelectInput } from '../SelectInput'
export default function EditTicketForm ({
  onSubmit,
  ticketTypes,
  resourceTypes,
  metric,
  isFetching,
  billingStatuses,
  initialValues,
  ticketTypeBillingStatusSelected,
  setTicketTypeBillingStatusSelected,
  resourceTypeBillingStatusSelected,
  setResourceTypeBillingStatusSelected,
  weightOptions,
  serviceFrequency,
  recurringTicketValues,
  feeTypes,
  ticketFeeValues,
  users
}) {
  const { hauler } = useSelector(({ user: { hauler } }) => ({ hauler }))
  const isRecurring = serviceFrequency === 'RECURRING' && hauler.isRecurringServiceEnabled

  function onTicketTypeChange (val, prevVal) {
    const selected = ticketTypes.find(type => type.id === val)
    const previouslySelected = resourceTypes.find(type => type.id === prevVal)
    const selectedInOptions = Object.keys(billingStatuses).includes(selected?.defaultBillingStatus)
    const prevSelectedInOptions = Object.keys(billingStatuses).includes(previouslySelected?.defaultBillingStatus)
    if (val) {
      if (selectedInOptions) {
        setTicketTypeBillingStatusSelected(selected?.defaultBillingStatus)
        return resourceTypeBillingStatusSelected || selected?.defaultBillingStatus || 'PENDING'
      } else { /* handles for case where the ticket type's default billing status is not present in the billing status select options if the ticket has an associated invoice */
        setTicketTypeBillingStatusSelected(null)
        return resourceTypeBillingStatusSelected ||
        (ticketTypeBillingStatusSelected
          ? initialValues.billingStatus
          : prevSelectedInOptions ? previouslySelected?.defaultBillingStatus : initialValues.billingStatus) ||
          initialValues.billingStatus
      }
    }
    return null
  }

  function onResourceTypeChange (val, prevVal) {
    const selected = resourceTypes.find(type => type.id === val)
    const previouslySelected = resourceTypes.find(type => type.id === prevVal)
    const selectedInOptions = Object.keys(billingStatuses).includes(selected?.defaultBillingStatus)
    const prevSelectedInOptions = Object.keys(billingStatuses).includes(previouslySelected?.defaultBillingStatus)
    if (val) {
      if (selectedInOptions) {
        setResourceTypeBillingStatusSelected(selected?.defaultBillingStatus)
        return selected?.defaultBillingStatus || ticketTypeBillingStatusSelected || 'PENDING'
      } else { /* handles for case where the resource type's default billing status is not present in the billing status select options if the ticket has an associated invoice */
        setResourceTypeBillingStatusSelected(null)
        return ticketTypeBillingStatusSelected ||
          (resourceTypeBillingStatusSelected
            ? initialValues.billingStatus
            : prevSelectedInOptions ? previouslySelected?.defaultBillingStatus : initialValues.billingStatus) ||
            initialValues.billingStatus
      }
    }
    return null
  }

  function billingStatusMessaging (manuallyUpdated, value, formState) {
    const notEdited = (initialValues.billingStatus === value) && !formState.dirtyFields?.billingStatus
    if (manuallyUpdated || !value || notEdited) {
      return ''
    } else if (value === resourceTypeBillingStatusSelected) {
      return 'Using asset type default'
    } else if (value === ticketTypeBillingStatusSelected) {
      return 'Using ticket type default'
    } else {
      return 'Using general default'
    }
  }

  function formatDate (date) {
    return dayjs(date).format('YYYY-MM-DD')
  }

  const resetAssetDetails = (form) => {
    form.change('resourceTypeId', null)
    form.change('serviceInterval', null)
    form.change('serviceDay', null)
    form.change('serviceDate', null)
    form.change('startDate', null)
    form.change('endDate', null)
  }

  const initialFormValues = isRecurring ? { ...initialValues, ...recurringTicketValues, ...ticketFeeValues } : initialValues

  const serviceDateOptions = Array.from({ length: 31 }, (_, i) => ({
    value: i + 1,
    label: String(i + 1)
  }))

  function clearDescription (args, state) {
    const [ticketFeeIndex] = args
    state.fields[`${ticketFeeIndex}.description`].change(null)
  }

  return (
    <div className='dis-panel'>
      <div className='dis-panel-body'>
        <Form
          mutators={{ ...arrayMutators, clearDescription }}
          onSubmit={onSubmit}
          initialValues={initialFormValues}
          validate={validate(isRecurring)}>
          {({ handleSubmit, pristine, form, valid, values }) => {
            const formState = form.getState()
            const billingStatusManuallyUpdated = formState.dirtyFields?.billingStatus && formState.visited?.billingStatus
            return (
              <form onSubmit={handleSubmit}>
                <div className={classNames({ 'dis-form-row': isRecurring })}>
                  <Field name='billingStatus' subscription={{}}>
                    {(
                      { input: { onChange } }
                    ) => (
                      <OnChange name='ticketTypeId'>
                        {(val, prevVal) => {
                          if (!billingStatusManuallyUpdated) {
                            onChange(onTicketTypeChange(val, prevVal))
                          }
                        }}
                      </OnChange>
                    )}
                  </Field>

                  <Field name='billingStatus' subscription={{}}>
                    {(
                      { input: { onChange } }
                    ) => (
                      <OnChange name='resourceTypeId'>
                        {(val, prevVal) => {
                          if (!billingStatusManuallyUpdated) {
                            onChange(onResourceTypeChange(val, prevVal))
                          }
                        }}
                      </OnChange>
                    )}
                  </Field>
                  <Field name='ticketTypeId'>
                    {({ input }) => (
                      <div className={classNames('form-group', { 'dis-third-width-field': isRecurring })}>
                        <label htmlFor='ticketTypeId' className='control-label'>Ticket Type</label>
                        <select
                          {...input}
                          type='select'
                          className='form-control'>
                          <option key='placeholder' value=''></option>
                          {ticketTypes.map((tt) =>
                            (
                              <option
                                value={tt.id}
                                key={tt.id}>
                                {`${tt.name} (${tt.shortCode})`}
                              </option>
                            )
                          )}
                        </select>
                      </div>
                    )}
                  </Field>

                  <Field name='resourceTypeId'>
                    {({ input }) => (
                      <div className={classNames('form-group', { 'dis-third-width-field': isRecurring })}>
                        <label htmlFor='resourceTypeId' className='control-label'>Asset Type</label>
                        <select
                          {...input}
                          type='select'
                          className='form-control'>
                          <option key='placeholder' value=''></option>
                          {resourceTypes.map((rt) =>
                            (
                              <option
                                value={rt.id}
                                key={rt.id}>
                                {`${rt.name} (${rt.shortCode})`}
                              </option>
                            )
                          )}
                        </select>
                      </div>
                    )}
                  </Field>

                  <Field name='billingStatus'>
                    {({ input }) => (
                      <div className={classNames('form-group', { 'dis-third-width-field': isRecurring })}>
                        <label htmlFor='billingStatus' className='control-label'>Billing Status</label>
                        <select
                          {...input}
                          type='select'
                          className='form-control'>
                          <option key='placeholder' value=''></option>
                          {Object.keys(billingStatuses).map((opt) =>
                            (
                              <option
                                value={opt}
                                key={opt}>
                                {enumToTitleCase(opt)}
                              </option>
                            )
                          )}
                        </select>
                        <h6 className={styles.inputMessaging}>
                          <em>{billingStatusMessaging(billingStatusManuallyUpdated, values.billingStatus, formState)}</em>
                        </h6>
                      </div>
                    )}
                  </Field>
                </div>

                <div className={classNames({ 'dis-form-row': isRecurring })}>
                  {!isRecurring &&
                    <Field
                      name='weight'
                      label={'Weight'}
                      component={TextInput}
                    />
                  }

                  <Field name='weightUnitsOfMeasure'>
                    {({ input }) => (
                      <div className={classNames('form-group', { 'dis-third-width-field': isRecurring })}>
                        <label htmlFor='weightUnitsOfMeasure' className='control-label'>Weight Unit</label>
                        <select
                          {...input}
                          type='select'
                          className='form-control'>
                          {weightOptions?.map((options) =>
                            (
                              <option
                                value={options.value}
                                key={options.value}>
                                {options.label}
                              </option>
                            )
                          )}
                        </select>
                      </div>
                    )}
                  </Field>
                  {!isRecurring &&
                    <Field
                      name='weightTicketNumber'
                      label={'Weight Ticket Number'}
                      component={TextInput}
                    />
                  }
                </div>

                {(isRecurring && values.resourceTypeId) && (
                  <div className={'dis-rounded-panel'}>
                    <div className='dis-asset-header'>
                      <label className='dis-list-item-header'>
                        {resourceTypes.find(r => r.id === values.resourceTypeId)?.name}
                      </label>
                      <button
                        type='button'
                        onClick={() => resetAssetDetails(form)}>
                        <i className={classNames('material-icons dis-btn-icon', styles.closeIcon)}>clear</i>
                      </button>
                    </div>
                    <div className='dis-form-container padding-1em'>
                      <div className='dis-form-row'>
                        <Field name='serviceInterval'>
                          {({ input }) => (
                            <div className='form-group dis-half-width-field'>
                              <label className='control-label' htmlFor='serviceInterval'>Select Interval</label>
                              <select
                                {...input}
                                type='select'
                                className='form-control'>
                                <option key='placeholder' value=''></option>
                                {Object.entries({
                                  WEEKLY: 'Weekly',
                                  MONTHLY: 'Monthly',
                                  EVERY_X_DAYS: 'Every x days'
                                }).map(([key, value]) =>
                                  <option key={key} value={key}>{value}</option>)}
                              </select>
                            </div>
                          )}
                        </Field>
                        {values.serviceInterval !== 'MONTHLY'
                          ? values.serviceInterval !== 'EVERY_X_DAYS'
                            ? <Field name='serviceDay'>
                              {({ input }) => (
                                <div className='form-group dis-half-width-field'>
                                  <label htmlFor='serviceDay' className='control-label'>Select Service Day(s)</label>
                                  <UnderlineSelect
                                    placeholder={''}
                                    isMulti
                                    options={dayOptions}
                                    defaultValue={dayOptions.filter(opt =>
                                      values?.serviceDay?.includes(opt.value)
                                    )}
                                    onChange={(selectedOptions) => {
                                      const selected = selectedOptions.map(o => o.value) || []
                                      form.change('serviceDay', selected)
                                    }}
                                    minWidth={315}
                                    closeMenuOnSelect
                                  />
                                </div>
                              )}
                            </Field>
                            : <div className='dis-half-width-field'>
                              <Field
                                name='serviceDailyFrequency'
                                label={'Service Daily Frequency'}
                                component={TextInput}
                                type='number'
                                min={1}
                              />
                            </div>
                          : <Field name='serviceDate'>
                            {({ input }) => (
                              <div className='dis-half-width-field'>
                                <label className='control-label' htmlFor='serviceDate'>Select Service Date</label>
                                <UnderlineSelect
                                  placeholder=''
                                  inputId='serviceDate'
                                  isMulti
                                  options={serviceDateOptions}
                                  defaultValue={serviceDateOptions.filter(opt =>
                                    values?.serviceDate?.includes(opt.value)
                                  )}
                                  onChange={(selectedOptions) => {
                                    const selected = selectedOptions?.map(o => o.value) || []
                                    input.onChange(selected)
                                  }}
                                  minWidth={315}
                                  closeMenuOnSelect
                                />
                              </div>
                            )}
                          </Field>
                          }
                      </div>

                      <div className='dis-form-row'>
                        <Field name='startDate'>
                          {({ input }) => (
                            <div className='dis-half-width-field'>
                              <label className={classNames('control-label', styles.dateLabel)} htmlFor='startDate'>
                                Starting On
                                <DatePicker
                                  className={styles.datePicker}
                                  dateFormat='MM/dd/yyyy'
                                  selected={
                                    input.value && dayjs(input.value, 'YYYY-MM-DD', true).isValid()
                                      ? dayjs(input.value, 'YYYY-MM-DD').toDate()
                                      : null
                                  }
                                  onChange={(date) =>
                                    input.onChange(date ? dayjs(date).format('YYYY-MM-DD') : null)
                                  }
                                  maxDate={values.endDate ? new Date(formatDate(values.endDate)) : undefined}
                                  minDate={new Date()}
                                />
                              </label>
                            </div>
                          )}
                        </Field>
                        <Field name='endDate'>
                          {({ input }) => (
                            <div className='dis-half-width-field'>
                              <label className={classNames('control-label', styles.dateLabel)} htmlFor='endDate'>
                                Ending On
                                <DatePicker
                                  className={styles.datePicker}
                                  dateFormat='MM/dd/yyyy'
                                  selected={
                                    input.value && dayjs(input.value, 'YYYY-MM-DD', true).isValid()
                                      ? dayjs(input.value, 'YYYY-MM-DD').toDate()
                                      : null
                                  }
                                  onChange={(date) => input.onChange(date ? dayjs(date).format('YYYY-MM-DD') : null)}
                                  minDate={values.startDate ? new Date(formatDate(values.startDate)) : undefined}
                                />
                              </label>
                            </div>
                          )}
                        </Field>
                      </div>

                      <div className='dis-form-row'>
                        <div className='dis-half-width-field padding-right-1rem'>
                          <Field
                            name='driverId'
                            label='Assigned Driver'
                            alphabetize
                            options={userSelectOptions(users)}
                            component={SelectInput}
                          />
                        </div>
                      </div>
                    </div>

                    <AssetFeesCard
                      feeTypes={feeTypes}
                      form={form}
                    />
                  </div>
                )}
                <Field
                  component={TextareaInput}
                  label='Billing Notes (Optional)'
                  name='billingNotes'
                  placeholder='(Billing Notes can be used to track quoted prices or any other billing information.
                  Drivers do not see billing notes on the mobile app.)'
                  maxLength='1000'
                />

                <div className={styles.requestedTimes}>
                  <div className={styles.timeInput}>
                    <Field
                      className='pull-left'
                      name='requestedStartTime'
                      label='Requested Time Range'
                      component={TimeInput}
                    />
                  </div>
                  <div className={styles.timeInput}>
                    <Field
                      className='pull-left'
                      name='requestedEndTime'
                      label='Requested Time Range'
                      component={TimeInput}
                    />
                  </div>
                </div>
                <div className={styles.buttonContainer}>
                  <button
                    type='submit'
                    className='dis-btn dis-btn-primary dis-btn-lg'
                    disabled={pristine || isFetching || !valid}>
                    Save Changes
                    <i className='material-icons dis-btn-icon'>check</i>
                  </button>
                </div>

              </form>
            )
          }}
        </Form>
      </div>
      <br />
    </div>
  )
}

EditTicketForm.propTypes = {
  isFetching: PropTypes.bool,
  metric: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool,
  resourceTypes: PropTypes.array.isRequired,
  ticketTypes: PropTypes.array.isRequired,
  billingStatuses: PropTypes.object.isRequired,
  initialValues: PropTypes.object,
  ticketTypeBillingStatusSelected: PropTypes.string,
  setTicketTypeBillingStatusSelected: PropTypes.func,
  resourceTypeBillingStatusSelected: PropTypes.string,
  setResourceTypeBillingStatusSelected: PropTypes.func,
  weightOptions: PropTypes.array.isRequired,
  serviceFrequency: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  recurringTicketValues: PropTypes.shape({
    endDate: PropTypes.string,
    serviceDate: PropTypes.string,
    serviceDay: PropTypes.array,
    serviceInterval: PropTypes.string,
    startDate: PropTypes.string,
    serviceDailyFrequency: PropTypes.number
  }),
  feeTypes: PropTypes.array.isRequired,
  ticketFeeValues: PropTypes.object,
  users: PropTypes.array
}
